import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ClassroomFilterDto } from '../../../../data/models/dto/classroom-filter.dto';
import { Profile } from '../../../../data/models/interfaces/profile.interface';
import { ClassRoomsService } from '../../../../data/services/classroom.service';
import { ProfileService } from '../../../../data/services/profile.service';
import {
  ExcludeProfessorAndEmployees,
  formatSelectedProfilesText,
} from 'src/app/data/utils/profile.utils';
import { UserUtil } from '../../../../data/utils/user.utils';
import { FlatNode } from '../../../../ui/components/select-hierarquical-list/model/flat.node.interface';
import { SimpleNode } from '../../../../ui/components/select-hierarquical-list/model/simple.node.interface';
import { ImageItem } from '../../../../ui/components/select-simple-list/model/item-image.interface';
import { Icon, MaterialIconsToLink } from '../../models/icons';
import { Link } from '../../models/link.interface';

@Component({
  selector: 'app-link-form',
  templateUrl: './link-form.component.html',
  styleUrls: ['./link-form.component.scss'],
})
export class LinkFormComponent implements OnInit {
  @Output() onUpdateLink = new EventEmitter<Link>();
  @Output() onCreateLink = new EventEmitter<Link>();

  isUpdate = this.data.isUpdate;

  link?: Link = this.data.link;

  idSchool: string = this.data.idSchool;

  validation_messages = {
    name: [
      { type: 'required', message: 'Informe o nome do atalho' },
      {
        type: 'maxlength',
        message: 'O nome do atalho deve ter no máximo 34 caracteres',
      },
    ],
    path: [
      { type: 'required', message: 'Informe o link (endereço) do atalho' },
      { type: 'pattern', message: 'Link inválido' },
    ],
    icon: [{ type: 'required', message: 'Selecione um ícone' }],
    profiles: [{ type: 'required', message: 'Selecione um Perfil de acesso' }],
  };

  urlRegex =
    /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/;
  formGroup: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(34)]],
    path: ['', [Validators.required, Validators.pattern(this.urlRegex)]],
    icon: ['', [Validators.required]],
    profiles: ['', [Validators.required]],
    classrooms: ['', [Validators.required]],
    year: ['', [Validators.required]]
  });

  profilePlaceholder = 'Perfis de acesso';
  selectAll = 'Todos os perfis';
  idsProfiles: string[] = [];
  profiles: Profile[] = [];
  integration: boolean = false;
  years: string[] = []

  hasClassrooms: boolean = true;
  alertMessage = 'Este atalho não poderá ser criado pois ainda não existem turmas cadastradas para o ano letivo selecionado.';

  materialIcons: Icon[] = MaterialIconsToLink();

  idsSchool = this.userUtil.getSchools().map((s) => s.id);

  excludeProfiles = ExcludeProfessorAndEmployees;

  segmentTreeData: SimpleNode[] = [];
  idsClassrooms: string[] = [];

  constructor(
    private readonly fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA)
    private readonly data: {
      link?: Link;
      idSchool: string;
      profiles: ImageItem[];
      isUpdate: boolean;
    },
    private profileService: ProfileService,
    private classroomsService: ClassRoomsService,
    private userUtil: UserUtil
  ) {}

  async ngOnInit() {
    await this.getYears();
    await this.loadProfiles();
    this.loadLinkData(this.link!);
    await this.loadSegmentTree();
    this.isIntegration(this.link!);
  }

  private async loadProfiles() {
    this.profiles = await this.profileService.getAll().toPromise();
  }

  private async getYears(): Promise<void> {
    const currentYear = new Date().getFullYear().toString();

    try {
      if (this.idSchool) {
        const result = await this.classroomsService
          .getYears({ idSchool: this.idSchool })
          .toPromise();
        if (result && result.length > 0) {
          this.years = result;
          if(!this.link){
            this.formGroup.controls['year'].setValue(this.years[0]);
          }
        }
      }
    } catch (error) {
      console.error(error);

      this.years = [currentYear];
      this.formGroup.controls['year'].setValue(currentYear);
    }

  }

  isIntegration(link: Link) {
    if (link?.integration) {
      this.integration = true;
    }
  }

  newLinkData() {
    this.link = {
      id: '',
      name: '',
      iconWeb: '',
      iconMobile: '',
      path: '',
      order: -1,
      idsClassRooms: [],
      idsProfiles: [],
      integration: undefined,
      idSchool: '',
      year: '',
    } as Link;
  }

  loadLinkData(link: Link) {
    if (link) {
      this.loadFormFields(link);
      this.setChoosenIcon(link.iconWeb);
      this.idsProfiles = link.idsProfiles;
      this.idsClassrooms = link.idsClassRooms!;
    } else {
      this.newLinkData();
    }
  }

  async loadFormFields(link: Link) {
    this.formGroup.controls['name'].setValue(link.name);
    this.formGroup.controls['path'].setValue(link.path);
    this.formGroup.controls['icon'].setValue(link.iconWeb);
    this.formGroup.controls['year'].setValue(link.year ?? this.years[0]);
    this.formGroup.controls['profiles'].setValue(
      await this.formatProfilesText(link.idsProfiles)
    );
  }

  async changeYear(year: string) {
    this.formGroup.controls['classrooms'].setValue('');
    this.formGroup.controls['year'].setValue(year);
    this.idsClassrooms = [];

    this.segmentTreeData = [];
    await this.loadSegmentTree();
  }

  private async formatProfilesText(
    selectedIdsProfiles: string[]
  ): Promise<string> {
    const result = await this.profileService.getAll().toPromise();
    const profiles = result.map(
      (profile) =>
        <ImageItem>{
          id: profile.id,
          imageUrl: '',
          name: profile.name,
          isChecked: selectedIdsProfiles.includes(profile.id!),
        }
    );
    const profileNames = formatSelectedProfilesText(profiles);
    return profileNames;
  }

  setChoosenIcon(icon: string) {
    const index = this.materialIcons.findIndex(
      (item) => item.iconWebName == icon
    );
    if (index >= 0) {
      this.materialIcons[index].checked = true;
    }
  }

  openList(evt: any, trigger: MatAutocompleteTrigger): void {
    evt.stopPropagation();
    if (trigger.panelOpen) {
      trigger.closePanel();
    } else {
      trigger.openPanel();
    }
  }

  handleProfilesSelect(evt: ImageItem[]): void {
    const names = formatSelectedProfilesText(evt);

    this.formGroup.controls['profiles'].setValue(names);

    this.idsProfiles = evt
      .filter((item) => item.isChecked)
      .map((item) => item.id) as string[];

    this.formGroup.markAsDirty();
  }

  saveLink() {
    const link = {
      id: this.link?.id,
      name: this.formGroup.value.name,
      path: this.formGroup.value.path,
      iconWeb: this.formGroup.value.icon,
      idsProfiles: this.idsProfiles,
      iconMobile: this.link?.iconMobile,
      order: this.link?.order,
      integration: this.link?.integration,
      idSchool: this.idsSchool[0],
      idsClassRooms: this.idsClassrooms,
      year: this.formGroup.value.year,
    } as Link;

    if (!this.isUpdate) {
      this.onCreateLink.emit(link);
    } else {
      this.onUpdateLink.emit(link);
    }
  }

  iconButtonToggleClicked(icon: Icon) {
    if (this.link!.iconMobile != icon.iconMobileName) {
      this.link!.iconMobile = icon.iconMobileName;
      this.formGroup.controls['icon'].setValue(icon.iconWebName);
      this.formGroup.markAsDirty();
    }
  }

  canSave(): boolean {
    if (this.integration)
      return this.formGroup.controls['profiles'].valid && this.formGroup.dirty;
    else return this.formGroup.valid && this.formGroup.dirty;
  }

  private async loadSegmentTree(): Promise<void> {
    const filter: ClassroomFilterDto = {
      year: this.formGroup.value.year,
      idSchool: this.idsSchool[0],
    };

    try {
      const tree = await this.classroomsService.getTree(filter).toPromise();
      this.segmentTreeData = tree.map((seg) => ({
        id: seg.id,
        name: seg.name,
        children: seg.series.map((ser) => ({
          name: ser.name,
          children: ser.classRooms.map((cls) => ({
            id: cls.id,
            name: cls.name,
          })),
        })),
      }));
      let classrooms: string[] = [];
      tree.forEach((node) =>
        node.series.forEach((serie) =>
          serie.classRooms.forEach((classroom) => {
            if (this.link?.idsClassRooms?.includes(classroom.id)) {
              classrooms.push(classroom.name);
            }
          })
        )
      );
      this.updateClassroomsControlState(classrooms);
    } catch (error: any) {
      this.hasClassrooms = false;

      if (error.status === 404) {
        this.segmentTreeData = [];
        this.idsClassrooms = [];

        this.updateClassroomsControlState();
        this.alertMessage;
      } else {
        console.error(error);
      }
    }
  }

  private updateClassroomsControlState(classrooms?: string[]): void {
    const control = this.formGroup.controls['classrooms']

    if (!this.hasClassrooms) {
      control?.setValue('');
      control?.disable({ emitEvent: false });
    } else {
      control?.enable({ emitEvent: false });
      control?.setValue(
        this.formatSelectedClassroomsText(classrooms ?? [])
      );
    }
  }

  handleSelectClassroom(classrooms: FlatNode[]) {
    const ids = classrooms.map((classroom) => classroom.id);
    this.idsClassrooms = ids;
    const names = classrooms.map((classroom) => classroom.name);
    this.formGroup.controls['classrooms'].setValue(
      this.formatSelectedClassroomsText(names)
    );
    this.formGroup.markAsDirty();
  }

  private formatSelectedClassroomsText(classrooms: string[]): string {
    let classroomsText = '';
    if (classrooms.length >= 1) {
      classroomsText = classrooms[0];
      if (classrooms.length > 1) {
        classroomsText = classroomsText
          .concat(' +')
          .concat((classrooms.length - 1).toString());
      }
    }
    return classroomsText;
  }
}
